import React, { useCallback, useEffect, useState } from "react";

// 관리자 · 결정례 정답지 — 세관 결정례를 AI 검수자가 다시 확인해 만든 평가·근거용 문제집(판정 로직과 무관).
// 구분: 확인된 정답(결정과 검수가 같고 현행 10자리까지 정해짐) · 바로잡은 정답(두 검수자가 같은 다른 답에 합의)
//       · 국내 실무 쟁점(검수자는 틀렸다 보나 국내 결정례가 일관되게 반대) · 제외(엇갈림·판단 불가).
const KINDS = ["확인된 정답", "바로잡은 정답", "국내 실무 쟁점", "제외"];
const PAGE = 50;

const authHeaders = () => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const formatCode = (code) => {
  const v = String(code || "");
  if (v.length === 10) return `${v.slice(0, 4)}.${v.slice(4, 6)}-${v.slice(6)}`;
  if (v.length === 6) return `${v.slice(0, 4)}.${v.slice(4)}`;
  return v || "—";
};

function Stat({ label, value, active, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{ ...S.stat, ...(active ? S.statOn : null) }} aria-pressed={active}>
      <span style={S.statValue}>{Number(value || 0).toLocaleString()}</span>
      <span style={S.statLabel}>{label}</span>
    </button>
  );
}

function Row({ it }) {
  const [open, setOpen] = useState(false);
  const differs = it.answer_hsk && it.decided_hsk && it.answer_hsk !== it.decided_hsk;
  return (
    <>
      <tr style={S.tr} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <td style={S.td}><span style={S.kind}>{it.kind}</span></td>
        <td style={{ ...S.td, ...S.mono }}>{it.id}</td>
        <td style={S.td}>{it.decision_date || "—"}</td>
        <td style={{ ...S.td, ...S.mono }}>{formatCode(it.decided_hsk)}</td>
        <td style={{ ...S.td, ...S.mono, fontWeight: 700, color: differs ? "#1d4ed8" : undefined }}>
          {formatCode(it.answer_hsk || it.answer_hs6)}
        </td>
        <td style={S.td}>{it.confidence || "—"}</td>
        <td style={{ ...S.td, maxWidth: 420 }}>
          <div style={S.clip}>{it.input_ko || it.title || "—"}</div>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} style={S.detail}>
            <div><b>제목</b> {it.title || "—"}</div>
            <div><b>정리한 입력</b> {it.input_ko || "—"} {it.input_sufficient === false && <span style={S.warn}>(입력만으로는 부족)</span>}</div>
            {Array.isArray(it.missing_facts) && it.missing_facts.length > 0 && <div><b>모자라는 사실</b> {it.missing_facts.join(" · ")}</div>}
            {Array.isArray(it.issues) && it.issues.length > 0 && <div><b>문제 표시</b> {it.issues.join(" · ")}</div>}
            {it.note && <div><b>검수 의견</b> {it.note}</div>}
            {it.cross_verdict && <div><b>교차 검수</b> {it.cross_verdict} — {it.cross_reason || ""}</div>}
            <div style={S.muted}>회차 {it.round} · 류 {it.chapter || "—"}
              {it.decision_url && <> · <a href={it.decision_url.startsWith("http") ? it.decision_url : `https://unipass.customs.go.kr${it.decision_url}`} target="_blank" rel="noopener noreferrer">결정례 원문 ↗</a></>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminAnswerKey({ API_BASE_URL = "" }) {
  const [summary, setSummary] = useState(null);
  const [kind, setKind] = useState("확인된 정답");
  const [chapter, setChapter] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/onto/admin/answer-key`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setSummary)
      .catch((e) => setErr(String(e.message || e)));
  }, [API_BASE_URL]);

  const load = useCallback(() => {
    const p = new URLSearchParams({ offset: String(offset), limit: String(PAGE) });
    if (kind) p.set("kind", kind);
    if (chapter.trim()) p.set("chapter", chapter.trim());
    if (q.trim()) p.set("q", q.trim());
    fetch(`${API_BASE_URL}/api/onto/admin/answer-key/items?${p}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { setData(d); setErr(null); })
      .catch((e) => setErr(String(e.message || e)));
  }, [API_BASE_URL, kind, chapter, q, offset]);

  useEffect(() => { load(); }, [load]);

  const byKind = summary?.by_kind || {};
  const total = data?.total ?? 0;
  return (
    <div style={S.wrap}>
      <div>
        <h2 style={S.h2}>결정례 정답지</h2>
        <p style={S.lead}>
          세관 결정례를 AI 검수자가 다시 확인해 만든 채점용 문제집입니다. 판정 로직에는 쓰지 않습니다.
          {summary?.loaded_at && <> 마지막 적재 {summary.loaded_at.slice(0, 16).replace("T", " ")} · 검수 {Number(summary.total || 0).toLocaleString()}건</>}
        </p>
      </div>
      <div style={S.stats}>
        {KINDS.map((k) => (
          <Stat key={k} label={k} value={byKind[k]} active={kind === k} onClick={() => { setKind(kind === k ? "" : k); setOffset(0); }} />
        ))}
      </div>
      <form style={S.filters} onSubmit={(e) => { e.preventDefault(); setOffset(0); load(); }}>
        <label style={S.label}>류 <input aria-label="류" value={chapter} onChange={(e) => setChapter(e.target.value)} placeholder="예: 85" style={{ ...S.input, width: 70 }} /></label>
        <label style={S.label}>검색 <input aria-label="검색" value={q} onChange={(e) => setQ(e.target.value)} placeholder="품명·결정례 번호·세번" style={{ ...S.input, width: 240 }} /></label>
        <button type="submit" style={S.btn}>찾기</button>
        <span style={S.muted}>{total.toLocaleString()}건</span>
      </form>
      {err && <div style={S.err}>불러오지 못했습니다: {err}</div>}
      <div style={{ overflowX: "auto" }}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>구분</th><th style={S.th}>결정례</th><th style={S.th}>결정일</th>
              <th style={S.th}>세관 결정</th><th style={S.th}>정답</th><th style={S.th}>확신</th><th style={S.th}>정리한 입력</th>
            </tr>
          </thead>
          <tbody>{(data?.items || []).map((it) => <Row key={it.id} it={it} />)}</tbody>
        </table>
      </div>
      <div style={S.pager}>
        <button type="button" style={S.btn} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>이전</button>
        <span style={S.muted}>{total ? `${offset + 1}–${Math.min(offset + PAGE, total)} / ${total.toLocaleString()}` : "—"}</span>
        <button type="button" style={S.btn} disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>다음</button>
      </div>
    </div>
  );
}

const S = {
  wrap: { display: "flex", flexDirection: "column", gap: 12, padding: 16, fontSize: 13, color: "#1f2937" },
  h2: { margin: 0, fontSize: 18 },
  lead: { margin: "4px 0 0", color: "#6b7280", fontSize: 12.5 },
  stats: { display: "flex", gap: 8, flexWrap: "wrap" },
  stat: { display: "flex", flexDirection: "column", alignItems: "flex-start", border: "1px solid #d9dee6", borderRadius: 6, padding: "6px 12px", background: "#fff", cursor: "pointer", minWidth: 120 },
  statOn: { borderColor: "#1d4ed8", background: "#eef4fc" },
  statValue: { fontSize: 18, fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  statLabel: { fontSize: 12, color: "#6b7280" },
  filters: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  label: { display: "inline-flex", gap: 6, alignItems: "center", color: "#6b7280", fontSize: 12.5 },
  input: { border: "1px solid #d9dee6", borderRadius: 4, padding: "3px 6px", font: "inherit" },
  btn: { border: "1px solid #d9dee6", borderRadius: 4, padding: "3px 10px", background: "#fff", color: "#1d4ed8", cursor: "pointer", font: "inherit" },
  table: { borderCollapse: "collapse", width: "100%", minWidth: 900 },
  th: { textAlign: "left", fontSize: 12, color: "#6b7280", borderBottom: "1px solid #d9dee6", padding: "4px 8px", whiteSpace: "nowrap" },
  tr: { cursor: "pointer", borderBottom: "1px solid #eef0f3" },
  td: { padding: "4px 8px", verticalAlign: "top" },
  mono: { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  kind: { fontSize: 11.5, border: "1px solid #d9dee6", borderRadius: 3, padding: "0 5px", whiteSpace: "nowrap" },
  clip: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  detail: { background: "#f7f9fc", padding: "8px 12px", lineHeight: 1.7, fontSize: 12.5, borderBottom: "1px solid #d9dee6" },
  muted: { color: "#6b7280", fontSize: 12 },
  warn: { color: "#6b7280" },
  err: { color: "#1d4ed8", fontSize: 12.5 },
  pager: { display: "flex", gap: 10, alignItems: "center" },
};
